// src/pages/DecisionEnginePage.jsx
// ============================================================
// Decision Engine — Predict → Prioritize → Execute pipeline
// Ranks all tenants by Expected Recovery Value (ERV) and
// groups them by pipeline stage based on outreach history.
// ============================================================
import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useProperty } from '../hooks/useProperty';
import { useTenants } from '../hooks/useTenants';
import PropertySelector from '../components/PropertySelector';
import { calculatePortfolioERV, flattenOutreachLogs } from '../utils/ervEngine';
import { buildSentimentMap, SENTIMENT_CONFIG } from '../utils/sentimentAnalysis';
import { formatCurrency } from '../utils/formatters';

// ---- Recovery probability bar ----
function RecoveryBar({ probability }) {
  const color = probability >= 70 ? '#0F6E56' : probability >= 40 ? '#EF9F27' : '#ef4444';
  return (
    <div className="flex items-center gap-2 w-full">
      <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500"
          style={{ width: `${probability}%`, backgroundColor: color }} />
      </div>
      <span className="text-[11px] font-bold tabular-nums w-8 text-right" style={{ color }}>
        {probability}%
      </span>
    </div>
  );
}

// ---- Urgency badge ----
function UrgencyBadge({ urgency }) {
  const config = {
    critical: { bg: 'bg-red-100', text: 'text-red-700', label: 'CRITICAL', dot: 'bg-red-500 animate-pulse' },
    high:     { bg: 'bg-orange-100', text: 'text-orange-700', label: 'HIGH', dot: 'bg-orange-500' },
    medium:   { bg: 'bg-amber-100', text: 'text-amber-700', label: 'MEDIUM', dot: 'bg-amber-500' },
    low:      { bg: 'bg-green-100', text: 'text-green-700', label: 'LOW', dot: 'bg-green-500' },
  };
  const c = config[urgency];
  if (!c) return null;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${c.bg} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {c.label}
    </span>
  );
}

// ---- Factor pill ----
function FactorPill({ label, impact }) {
  const styles = {
    positive: 'bg-green-50 text-green-700 border-green-200',
    negative: 'bg-red-50 text-red-600 border-red-200',
    neutral:  'bg-gray-50 text-gray-500 border-gray-200',
  };
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${styles[impact] || styles.neutral}`}>
      {impact === 'positive' ? '↑' : impact === 'negative' ? '↓' : '·'} {label}
    </span>
  );
}

// ---- Pipeline stage card ----
function StageCard({ stage, label, icon, count, totalErv, isActive, onClick }) {
  const stageColors = {
    predict:    { border: 'border-blue-400', bg: 'bg-blue-50', text: 'text-blue-700', bar: 'bg-blue-400' },
    prioritize: { border: 'border-amber-400', bg: 'bg-amber-50', text: 'text-amber-700', bar: 'bg-amber-400' },
    execute:    { border: 'border-tenafi-green', bg: 'bg-tenafi-light', text: 'text-tenafi-green', bar: 'bg-tenafi-green' },
  };
  const c = stageColors[stage];
  return (
    <button
      onClick={onClick}
      className={"relative flex-1 rounded-2xl border-2 p-4 text-left transition-all duration-200 " +
        (isActive ? `${c.border} ${c.bg} shadow-md` : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm')}>
      {/* Arrow connector */}
      {stage !== 'predict' && (
        <div className="absolute -left-5 top-1/2 -translate-y-1/2 text-gray-300 text-xl font-light hidden lg:block">→</div>
      )}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xl">{icon}</span>
        <span className={"text-xs font-bold uppercase tracking-wider " + (isActive ? c.text : 'text-gray-500')}>{label}</span>
      </div>
      <p className={"text-2xl font-bold " + (isActive ? c.text : 'text-gray-900')}>{count}</p>
      <p className="text-xs text-gray-500 mt-0.5">{formatCurrency(totalErv)} recoverable</p>
      {isActive && <div className={"absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl " + c.bar} />}
    </button>
  );
}

// ---- Expandable tenant row ----
function TenantRow({ result, propertyId }) {
  const [expanded, setExpanded] = useState(false);
  const sentConfig = result.lastSentiment ? SENTIMENT_CONFIG[result.lastSentiment.sentiment] : null;

  return (
    <div className={"rounded-2xl border transition-all duration-200 " +
      (expanded ? 'border-tenafi-green/40 shadow-md bg-tenafi-light/30' : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm')}>
      {/* Summary row */}
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center gap-3 p-4 text-left">
        {/* Rank */}
        <div className={"w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 " +
          (result.rank <= 3 ? 'bg-tenafi-green text-white' : 'bg-gray-100 text-gray-500')}>
          #{result.rank}
        </div>

        {/* Tenant info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={`/tenant/${propertyId}/${result.tenantId}`}
              onClick={e => e.stopPropagation()}
              className="text-sm font-semibold text-gray-900 hover:text-tenafi-green transition-colors">
              Unit {result.unitNumber} — {result.tenantName}
            </Link>
            <UrgencyBadge urgency={result.urgency} />
            {sentConfig && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${sentConfig.bg} ${sentConfig.text} ${sentConfig.border}`}>
                {sentConfig.icon} {sentConfig.label}
              </span>
            )}
          </div>
          <p className="text-[11px] text-gray-400 mt-0.5">
            {result.daysSinceLastOutreach !== null ? `Last contacted ${result.daysSinceLastOutreach}d ago` : 'No outreach yet'}
          </p>
        </div>

        {/* ERV */}
        <div className="text-right shrink-0 w-28">
          <p className="text-sm font-bold text-tenafi-green">{formatCurrency(result.ervDollars)}</p>
          <p className="text-[11px] text-gray-400">of {formatCurrency(result.outstandingBalance)}</p>
        </div>

        {/* Recovery bar (desktop) */}
        <div className="w-32 shrink-0 hidden sm:block">
          <RecoveryBar probability={result.recoveryProbability} />
        </div>

        {/* Chevron */}
        <svg className={"w-4 h-4 text-gray-400 transition-transform shrink-0 " + (expanded ? 'rotate-180' : '')}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 mt-0 space-y-3 pt-3">
          {/* Mobile recovery bar */}
          <div className="sm:hidden">
            <p className="text-[10px] font-medium text-gray-500 mb-1">Recovery Probability</p>
            <RecoveryBar probability={result.recoveryProbability} />
          </div>

          {/* Recommended action */}
          <div className="rounded-xl bg-blue-50 border border-blue-200/60 p-3">
            <p className="text-[10px] font-bold text-blue-500 uppercase tracking-wider mb-1">🎯 Recommended Action</p>
            <p className="text-xs text-blue-900">{result.recommendedAction}</p>
          </div>

          {/* ERV Factors */}
          {result.factors?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Contributing Factors</p>
              <div className="flex flex-wrap gap-1.5">
                {result.factors.slice(0, 7).map((f, i) => (
                  <FactorPill key={i} label={f.label} impact={f.impact} />
                ))}
              </div>
            </div>
          )}

          {/* Sentiment summary */}
          {result.lastSentiment && (
            <div className={`rounded-xl p-3 border ${sentConfig?.bg} ${sentConfig?.border}`}>
              <p className={`text-[10px] font-bold mb-1 ${sentConfig?.text}`}>🧠 Last Response Sentiment</p>
              <p className="text-xs text-gray-700">{result.lastSentiment.summary}</p>
              {(result.lastSentiment.signals || []).length > 0 && (
                <ul className="mt-1.5 space-y-0.5">
                  {result.lastSentiment.signals.map((s, i) => (
                    <li key={i} className="text-[10px] text-gray-500">· {s}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Quick link */}
          <div className="flex gap-2">
            <Link
              to={`/tenant/${propertyId}/${result.tenantId}`}
              onClick={e => e.stopPropagation()}
              className="px-3 py-1.5 rounded-xl border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
              View Full Profile →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Main page ----
export default function DecisionEnginePage() {
  const { properties, selectedProperty, setSelectedProperty, loading: propsLoading } = useProperty();
  const { tenants, loading: tenantsLoading } = useTenants(selectedProperty?.id);
  const [activeStage, setActiveStage] = useState('all');
  const loading = propsLoading || tenantsLoading;

  // Build pipeline (memoised)
  const pipeline = useMemo(() => {
    if (!tenants.length) return null;
    const outreachLogs = flattenOutreachLogs(tenants);
    const tenantContext = new Map(tenants.map(t => [t.id, { daysLate: t.currentDaysLate || 0, riskScore: t.risk?.score || 0 }]));
    const sentimentMap = buildSentimentMap(outreachLogs, tenantContext);
    return calculatePortfolioERV(tenants, outreachLogs, sentimentMap);
  }, [tenants]);

  const displayedTenants = useMemo(() => {
    if (!pipeline) return [];
    if (activeStage === 'all') return [...pipeline.allResults];
    return pipeline[activeStage]?.tenants || [];
  }, [pipeline, activeStage]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Decision Engine</h1>
            <p className="text-sm text-gray-500 mt-0.5">Predict · Prioritize · Execute — AI-ranked recovery pipeline</p>
          </div>
          <PropertySelector properties={properties} selected={selectedProperty} onSelect={setSelectedProperty} />
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1,2,3].map(i => (
              <div key={i} className="bg-white rounded-2xl border border-gray-200 p-5 animate-pulse h-20" />
            ))}
          </div>
        ) : !pipeline ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-gray-200">
            <div className="text-4xl mb-3">📊</div>
            <p className="text-gray-700 font-medium">No tenant data yet.</p>
            <a href="/upload" className="mt-4 inline-block px-5 py-2.5 bg-tenafi-green text-white rounded-xl text-sm font-semibold hover:bg-tenafi-green-dark">
              Upload Rent Roll
            </a>
          </div>
        ) : (
          <>
            {/* Portfolio ERV Summary */}
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Expected Recovery Value</p>
                  <p className="text-3xl font-bold text-tenafi-green">{formatCurrency(pipeline.totalRecoverable)}</p>
                  <p className="text-xs text-gray-400 mt-0.5">of {formatCurrency(pipeline.totalOutstanding)} outstanding</p>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">{pipeline.portfolioRecoveryRate}%</p>
                    <p className="text-[10px] text-gray-400">Recovery Rate</p>
                  </div>
                  <div className="w-px h-10 bg-gray-200" />
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">
                      {pipeline.predict.count + pipeline.prioritize.count + pipeline.execute.count}
                    </p>
                    <p className="text-[10px] text-gray-400">Active Tenants</p>
                  </div>
                </div>
              </div>

              {/* Stacked recovery bar */}
              {pipeline.totalOutstanding > 0 && (
                <div className="mt-4">
                  <div className="h-3 rounded-full bg-gray-100 overflow-hidden flex">
                    <div className="h-full bg-tenafi-green transition-all duration-700"
                      style={{ width: `${(pipeline.execute.totalErv / pipeline.totalOutstanding) * 100}%` }}
                      title={`Execute: ${formatCurrency(pipeline.execute.totalErv)}`} />
                    <div className="h-full bg-tenafi-amber transition-all duration-700"
                      style={{ width: `${(pipeline.prioritize.totalErv / pipeline.totalOutstanding) * 100}%` }}
                      title={`Prioritize: ${formatCurrency(pipeline.prioritize.totalErv)}`} />
                    <div className="h-full bg-blue-400 transition-all duration-700"
                      style={{ width: `${(pipeline.predict.totalErv / pipeline.totalOutstanding) * 100}%` }}
                      title={`Predict: ${formatCurrency(pipeline.predict.totalErv)}`} />
                  </div>
                  <div className="flex items-center gap-4 mt-2">
                    {[
                      { label: 'Execute', color: 'bg-tenafi-green' },
                      { label: 'Prioritize', color: 'bg-tenafi-amber' },
                      { label: 'Predict', color: 'bg-blue-400' },
                    ].map(({ label, color }) => (
                      <div key={label} className="flex items-center gap-1.5">
                        <div className={`w-2 h-2 rounded-full ${color}`} />
                        <span className="text-[10px] text-gray-400">{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Pipeline stage selector */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 lg:gap-6">
              {[
                { stage: 'predict', label: 'Predict', icon: '🔮', count: pipeline.predict.count, totalErv: pipeline.predict.totalErv },
                { stage: 'prioritize', label: 'Prioritize', icon: '📊', count: pipeline.prioritize.count, totalErv: pipeline.prioritize.totalErv },
                { stage: 'execute', label: 'Execute', icon: '⚡', count: pipeline.execute.count, totalErv: pipeline.execute.totalErv },
              ].map(card => (
                <StageCard key={card.stage} {...card}
                  isActive={activeStage === card.stage}
                  onClick={() => setActiveStage(activeStage === card.stage ? 'all' : card.stage)} />
              ))}
            </div>

            {/* Tenant list */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-900">
                  {activeStage === 'all' ? 'All Tenants' : `${activeStage.charAt(0).toUpperCase() + activeStage.slice(1)} Stage`}
                  <span className="ml-2 text-xs font-normal text-gray-400">
                    ({displayedTenants.length}) — ranked by ERV
                  </span>
                </h2>
                {activeStage !== 'all' && (
                  <button onClick={() => setActiveStage('all')}
                    className="text-xs text-tenafi-green hover:underline">
                    Show all →
                  </button>
                )}
              </div>

              <div className="space-y-2">
                {displayedTenants.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
                    <div className="text-3xl mb-2">✨</div>
                    <p className="text-gray-500 text-sm">No tenants in this stage.</p>
                  </div>
                ) : (
                  displayedTenants.map(result => (
                    <TenantRow key={result.tenantId} result={result} propertyId={selectedProperty?.id} />
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
